/**
 * Rebate Strategy
 * Implements CampaignSectionStrategy for rebate behavior.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { RebatePanel } from '../rebate';
import CampaignGamesGrid from '../../MultiViewCampaignSection/components/CampaignGamesGrid';
import { CampaignSectionFallback } from '../../MultiViewCampaignSection/components';
import MultiCampaignsGrid from '../../defaults/MultiCampaignGrid/MultiCampaignGrid';

import {
  useItemsByCampaignType,
  REBATE_CAMPAIGN_TYPE,
} from 'features/campaigns';
import { useCampaignSectionBase } from '../../MultiViewCampaignSection/_hooks/useCampaignSection';
import { useCampaignPanelInfo } from '../../MultiViewCampaignSection/_hooks/useCampaignPanelInfo';
import type {
  CampaignSectionStrategy,
  CampaignSectionState,
} from '../../MultiViewCampaignSection/types';
import type ISectionSettings from 'types/models/ISectionSettings';
import type { CampaignSource } from '../../../types/campaign.types';

/** Hook implementation for rebate section state */
const useRebateSectionState = (
  data: ISectionSettings,
  isActive: boolean
): CampaignSectionState<CampaignSource> => {
  const { t } = useTranslation();
  const base = useCampaignSectionBase(data, isActive);

  const { items: campaigns } = useItemsByCampaignType(REBATE_CAMPAIGN_TYPE) as { items: CampaignSource[]; hasMany: boolean; hasItems: boolean };

  const isLoading = false;
  const isError = false;
  const error = null;

  const handleRetry = useCallback(() => {
    console.log('[RebateStrategy] Retry rebate');
  }, []);

  const isSingleItem = campaigns.length === 1;
  const activeCampaign = isSingleItem ? campaigns[0] : null;

  const containerProps = base.createContainerProps({
    'data-one-rebate': isSingleItem ? 'true' : 'false',
  });

  const panelInfo = useCampaignPanelInfo({
    campaignType: REBATE_CAMPAIGN_TYPE,
    data,
    items: campaigns,
  });

  return {
    t,
    viewMode: base.viewMode,
    isLoading,
    isError,
    error,
    panelInfo,
    items: campaigns,
    isSingleItem,
    activeCampaign,
    handleViewToggle: base.handleViewToggle,
    handleRetry,
    handleNavigateToLobby: base.handleNavigateToLobby,
    containerProps,
    itemCount: campaigns.length,
  };
};

/** Rebate Strategy Implementation */
export const rebateStrategy: CampaignSectionStrategy<CampaignSource> = {
  type: REBATE_CAMPAIGN_TYPE,

  useCampaignSection: useRebateSectionState,

  renderMultiItemsLayout: ({ items, name, isActive }) => (
    <MultiCampaignsGrid name={name} campaigns={items} isActive={isActive} />
  ),

  renderSingleItemLayout: ({ item, data, isActive }) => (
    <CampaignGamesGrid data={data} campaign={item} isActive={isActive} />
  ),

  renderPanel: (panelInfo) => <RebatePanel panelInfo={panelInfo} />,

  renderFallback: ({ sectionName, error, onRetry, viewMode }) => (
    <CampaignSectionFallback
      sectionName={sectionName}
      error={error}
      onRetry={onRetry}
      viewMode={viewMode}
      sectionType="rebate"
    />
  ),
};

// probably we don't need export const rebateStrategy and export default rebateStrategy
// fallow-ignore-next-line unused-export
export default rebateStrategy;
